import { createI18n } from 'vue-i18n'
import it from './it.json'
import en from './en.json'
import enErrors from '@volverjs/zod-vue-i18n/locales/v4/en.json'
import itErrors from '@volverjs/zod-vue-i18n/locales/v4/it.json'

export const SUPPORTED_LOCALES = ['it', 'en'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

function detectLocale(): SupportedLocale {
    const browserLang = navigator.language.split('-')[0]
    return (SUPPORTED_LOCALES as readonly string[]).includes(browserLang)
        ? (browserLang as SupportedLocale)
        : 'en'
}

/*
 * Setup i18n
 * https://vue-i18n.intlify.dev/
 */

export const i18n = createI18n<false>({
    locale: detectLocale(),
    legacy: false,
    globalInjection: true,
    fallbackWarn: false,
    messages: {
        it: {
            ...it,
            errors: { ...itErrors, ...it.errors },
        },
        en: {
            ...en,
            errors: { ...enErrors, ...en.errors },
        },
    },
    numberFormats: {
        it: {
            currencyEUR: {
                style: 'currency',
                currency: 'EUR',
                currencySign: 'accounting',
                notation: 'standard',
                maximumFractionDigits: 4,
                useGrouping: true,
            },
            currencyUSD: {
                style: 'currency',
                currency: 'USD',
                currencySign: 'accounting',
                notation: 'standard',
                maximumFractionDigits: 4,
                useGrouping: true,
            },
            EUR: {
                style: 'currency',
                currency: 'EUR',
                currencyDisplay: 'narrowSymbol',
                notation: 'standard',
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            USD: {
                style: 'currency',
                currency: 'USD',
                currencyDisplay: 'narrowSymbol',
                notation: 'standard',
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            decimal: {
                style: 'decimal',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            integer: {
                style: 'decimal',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
                useGrouping: true,
            },
            compact: {
                style: 'decimal',
                notation: 'compact',
                compactDisplay: 'short',
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
                useGrouping: true,
            },
            seconds: {
                style: 'unit',
                unit: 'second',
                unitDisplay: 'long',
                maximumFractionDigits: 1,
            },
        },
        en: {
            currencyEUR: {
                style: 'currency',
                currency: 'EUR',
                currencySign: 'accounting',
                notation: 'standard',
                maximumFractionDigits: 4,
                useGrouping: true,
            },
            currencyUSD: {
                style: 'currency',
                currency: 'USD',
                currencySign: 'accounting',
                notation: 'standard',
                maximumFractionDigits: 4,
                useGrouping: true,
            },
            EUR: {
                style: 'currency',
                currency: 'EUR',
                currencyDisplay: 'narrowSymbol',
                notation: 'standard',
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            USD: {
                style: 'currency',
                currency: 'USD',
                currencyDisplay: 'narrowSymbol',
                notation: 'standard',
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            decimal: {
                style: 'decimal',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                useGrouping: true,
            },
            integer: {
                style: 'decimal',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
                useGrouping: true,
            },
            compact: {
                style: 'decimal',
                notation: 'compact',
                compactDisplay: 'short',
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
                useGrouping: true,
            },
            seconds: {
                style: 'unit',
                unit: 'second',
                unitDisplay: 'long',
                maximumFractionDigits: 1,
            },
        },
    },
    datetimeFormats: {
        it: {
            short: {
                dateStyle: 'short',
            },
            'month-short': {
                month: 'short',
            },
            'date-medium': {
                dateStyle: 'medium',
            },
            'date-time': {
                dateStyle: 'short',
                timeStyle: 'short',
            },
            'date-time-medium': {
                dateStyle: 'medium',
                timeStyle: 'short',
            },
            time: {
                timeStyle: 'short',
            },
            'weekday-day': {
                weekday: 'short',
                day: 'numeric',
            },
        },
        en: {
            short: {
                dateStyle: 'short',
            },
            'month-short': {
                month: 'short',
            },
            'date-medium': {
                dateStyle: 'medium',
            },
            'date-time': {
                dateStyle: 'short',
                timeStyle: 'short',
            },
            'date-time-medium': {
                dateStyle: 'medium',
                timeStyle: 'short',
            },
            time: {
                timeStyle: 'short',
            },
            'weekday-day': {
                weekday: 'short',
                day: 'numeric',
            },
        },
    },
})
